import React from 'react';
import { Link } from 'react-router-dom';
import artwork from '../../assets/landing/productivity-comic-v2.webp';
import fallStart from '../../assets/landing/productivity-comic-fall-start.webp';
import fallImpact from '../../assets/landing/productivity-comic-fall-impact.webp';
import coffeeIdea from '../../assets/landing/productivity-comic-coffee-idea.webp';
import searchArt from '../../assets/landing/productivity-comic-search.webp';
import discoveryArt from '../../assets/landing/productivity-comic-discovery.webp';
import dataWork from '../../assets/landing/productivity-comic-data-work.webp';
import analysisWork from '../../assets/landing/productivity-comic-analysis-work.webp';
import toastStart from '../../assets/landing/productivity-comic-toast-start.webp';
import semploraIcon from '../../assets/Logo_Only_Icon.png';
import ComicBalloons, { type Balloon } from './ComicBalloons';
import '../../styles/ProductivityComic.scss';

type ComicPanel = { label: string; state: string; alt: string; balloons: Balloon[] };

// Balloons in reading order. x, y, w and tip are % of the panel, placed on the final drawing and checked on the
// opening one too (panels 4-6 change drawing while the first balloon is on screen): no faces covered.
// cue: seconds at which the balloon pops in.
export const comicPanels: ComicPanel[] = [
    {
        label: 'Troppi dati. Troppo poco tempo.', state: 'Una montagna di inserimento dati e analisi.',
        alt: 'Una pila di libri e documenti cade sulla scrivania, sorprendendo Luca e Sara.',
        balloons: [
            { speaker: 'Luca', text: 'Altre centinaia di record da importare a mano…', kind: 'speech', x: 22, y: 2.5, w: 40, tip: [44, 22], cue: 1.72 },
            { speaker: 'Sara', text: 'Non finiremo mai!', kind: 'shout', x: 61, y: 7, w: 36, tip: [76, 25], cue: 2.3 },
        ],
    },
    {
        label: 'La pausa che accende un’idea.', state: 'Stanchi, Luca e Sara si fermano e trovano un’idea.',
        alt: 'Luca e Sara, esausti davanti alle pratiche, bevono un caffè; a Sara viene un’idea.',
        balloons: [
            { speaker: 'Luca', text: 'Ci serve un altro caffè…', kind: 'tired', x: 3, y: 2.5, w: 34, tip: [29, 17], cue: .36 },
            { speaker: 'Sara', text: 'Aspetta: c’è un modo più semplice!', kind: 'speech', x: 34, y: 10, w: 27, tip: [62, 25], cue: 2.18 },
        ],
    },
    {
        label: 'La ricerca porta a Semplora.', state: 'Un unico posto per i tool di cui hanno bisogno.',
        alt: 'Luca e Sara scoprono Semplora e guardano felici i tool sul portatile.',
        balloons: [
            { speaker: 'Sara', text: 'Aspetta… è Semplora!', kind: 'speech', x: 3, y: 2.5, w: 34, tip: [27, 14], cue: 1.52 },
            { speaker: 'Luca', text: 'Finalmente, tutto quello che ci serve!', kind: 'speech', x: 50, y: 4.5, w: 46, tip: [66, 13], cue: 3.12 },
        ],
    },
    {
        label: 'I dati iniziano a scorrere.', state: 'Inserimento e analisi nello stesso flusso.',
        alt: 'Luca e Sara usano i tool per inserire dati e prepararne l’analisi; fogli e risultati scorrono sul portatile.',
        balloons: [
            { speaker: 'Luca', text: 'Inseriamo i dati da qui.', kind: 'speech', x: 3, y: 2.5, w: 34, tip: [30, 13], cue: .42 },
            { speaker: 'Sara', text: 'E l’analisi prende forma!', kind: 'speech', x: 56, y: 5.5, w: 40, tip: [70, 16], cue: 2.72 },
        ],
    },
    {
        label: 'Tutto fatto, molto prima.', state: 'Il lavoro richiesto è completo in una frazione del tempo.',
        alt: 'Luca e Sara controllano i risultati e festeggiano quando sul portatile appare un grande segno di completamento.',
        balloons: [
            { speaker: 'Luca', text: 'Ma ci volevano giorni!', kind: 'shout', x: 2, y: 2.5, w: 40, tip: [24, 17], cue: .42 },
            { speaker: 'Sara', text: 'E abbiamo già finito!', kind: 'speech', x: 62, y: 9, w: 34, tip: [75, 21], cue: 2.72 },
        ],
    },
    {
        label: 'Più tempo per ciò che conta.', state: 'Semplora. Il tuo lavoro, più semplice.',
        alt: 'Luca e Sara brindano con le tazze di caffè davanti alla scrivania finalmente ordinata.',
        balloons: [
            { speaker: 'Luca', text: 'Ora il caffè ce lo godiamo!', kind: 'speech', x: 3, y: 2.5, w: 36, tip: [27, 12], cue: 1.08 },
            { speaker: 'Sara', text: 'E abbiamo tempo per nuove idee.', kind: 'speech', x: 56, y: 6.5, w: 40, tip: [68, 18], cue: 2.02 },
        ],
    },
];

const BUBBLE_IN = .42;
const READ_AFTER_LAST_BUBBLE = 2;

/** Panel length in ms: enough to read title and bubbles, never less than 6 s, and at least 2 s after the last bubble. */
export function panelDuration(panel: number) {
    const story = comicPanels[panel];
    const words = [story.label, ...story.balloons.map(balloon => balloon.text)].join(' ').split(/\s+/).filter(Boolean).length;
    const lastCue = Math.max(...story.balloons.map(balloon => balloon.cue));
    const afterLastBubble = (lastCue + BUBBLE_IN + READ_AFTER_LAST_BUBBLE) * 1000;
    return Math.round(Math.max(6000, 2000 + 300 * words, afterLastBubble));
}

// Extra drawings shown in each panel on top of the sprite (artwork): the reel preloads the next panel's.
const PANEL_FRAMES: string[][] = [[fallStart, fallImpact], [coffeeIdea], [searchArt, discoveryArt, semploraIcon], [dataWork], [analysisWork], [toastStart]];
const preloaded = new Set<string>();

/** Starts downloading the drawings of a panel, so the cut to it never shows an empty frame. */
export function preloadPanel(panel: number) {
    for (const src of [artwork, ...(PANEL_FRAMES[panel] ?? [])]) {
        if (preloaded.has(src)) continue;
        preloaded.add(src);
        const image = new Image();
        image.decoding = 'async';
        image.src = src;
    }
}

export default function ProductivityComic({ panel }: { panel: number }) {
    const story = comicPanels[panel];
    const isLast = panel === comicPanels.length - 1;

    return <div className="sp-comic">
        <p className="sp-comic__title">{story.label}</p>
        <div className={`sp-comic__stage sp-comic__stage--${panel + 1}`} key={panel}>
            <div className="sp-comic__visual">
                <div className="sp-comic__art" role="img" aria-label={`${story.alt} ${story.state}`} style={{ backgroundImage: `url(${artwork})`, backgroundPosition: `${(panel % 3) * 50}% ${panel < 3 ? 0 : 100}%` }} />
                {panel === 0 && <>
                    <img className="sp-comic__fall-frame sp-comic__fall-frame--start" src={fallStart} alt="" aria-hidden="true" />
                    <img className="sp-comic__fall-frame sp-comic__fall-frame--impact" src={fallImpact} alt="" aria-hidden="true" />
                </>}
                {panel === 1 && <img className="sp-comic__scene-frame sp-comic__scene-frame--idea" src={coffeeIdea} alt="" aria-hidden="true" />}
                {panel === 2 && <>
                    <img className="sp-comic__discovery-frame sp-comic__discovery-frame--search" src={searchArt} alt="" aria-hidden="true" />
                    <div className="sp-comic__discovery-frame sp-comic__discovery-frame--found" style={{ backgroundImage: `url(${discoveryArt})` }} aria-hidden="true">
                        <span className="sp-comic__brand"><img src={semploraIcon} alt="" />Semplora</span>
                    </div>
                </>}
                {panel === 3 && <img className="sp-comic__scene-frame sp-comic__scene-frame--data" src={dataWork} alt="" aria-hidden="true" />}
                {panel === 4 && <img className="sp-comic__scene-frame sp-comic__scene-frame--analysis" src={analysisWork} alt="" aria-hidden="true" />}
                {panel === 5 && <img className="sp-comic__scene-frame sp-comic__scene-frame--toast" src={toastStart} alt="" aria-hidden="true" />}
            </div>
            {panel === 0 && <span className="sp-comic__crash" aria-hidden="true">PATATRAC!</span>}
            {panel === 1 && <span className="sp-comic__effect sp-comic__effect--idea" aria-hidden="true">DING!</span>}
            {panel === 5 && <span className="sp-comic__effect sp-comic__effect--toast" aria-hidden="true">TIN!</span>}
            <ComicBalloons balloons={story.balloons} />
            {isLast && <Link className="sp-comic__cta" to="/login">Prova anche tu · Entra in Semplora</Link>}
        </div>
    </div>;
}
